import React from "react";
import { Card, Box, Typography, useMediaQuery, Button } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";

import useThemeRender from "../../../theme/useThemeRender";
import {
  cardContainer,
  linkContainer,
  linkTypography,
  buttonStyle,
} from "./carousalStyle";
import PropTypes from "prop-types";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatDate } from "../../../utils/function";
import { useDispatch, useSelector } from "react-redux";

import {
  setSummaryDetails,
  setViewPageName,
  setViewSummaryDrawer,
} from "../../../slices/viewSummarySlice";
import LazyImage from "../../../utils/LazyImage";
import useResize from "../../../hooks/useResize";
import Chapter from "../Chapter";
import RelevanceScore from "../RelevanceScore";
export default function CardComponent({
  card,
  content,
  flexDirection,
  heading,
  cardButtonDisable,
  chapter,
  handleAddFavourite,
  handleNavigate,
  isFavourite,
}) {
  const isDarkTheme = useSelector((store) => store.theme);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isMedium = useMediaQuery("(min-width:600px) and (max-width:1300px)");
  const insideCardBackgroundColorLayer1 = useThemeRender(
    "insideCardBackgroundColorLayer1"
  );

  const textColorLayer2 = useThemeRender("textColorLayer2");
  const boxShadowlayerColor1 = useThemeRender("boxShadowlayerColor1");

  const { isZoomed } = useResize();
  const dispatch = useDispatch();

  const handleOpenSummary = (details) => {
    dispatch(setViewSummaryDrawer(true));
    dispatch(setViewPageName(content));
    dispatch(setSummaryDetails(details));
  };

  let displayCount;

  if (heading === "In the Spotlight") {
    displayCount = card?.viewCount;
  } else if (card?.counts || card?.views) {
    displayCount = card?.counts ? card?.counts : card?.views;
  } else {
    displayCount = 0;
  }
  const width = isMobile ? "100%" : isMedium ? "48.5%" : "32.5%";

  const getFormattedDate = (heading, card, formatDate) => {
    if (chapter) {
      return formatDate(card?.publicationDate);
    }
    if (heading === "In the Spotlight") {
      return formatDate(card?.uploadedDate);
    }
    if (card?.uploadedOn) {
      return formatDate(card.uploadedOn);
    }
    return formatDate(card?.date);
  };

  const cardContentType =
    card?.contentType || card?.content || content || "Document";

  // Normalize (e.g., DOCUMENT → Document)
  const formattedType =
    cardContentType?.charAt(0)?.toUpperCase() +
    cardContentType?.slice(1)?.toLowerCase();

  // Handle plural → singular conversion (Documents → Document)
  const singularType = formattedType?.replace(/s$/, "");

  // Check if it’s a link
  const isLink = singularType?.toLowerCase() === "link";

  return (
    <Card
      sx={{
        width: width,
        flex: "0 0 auto",
        boxShadow: isDarkTheme
          ? `0px 0px 24px 0px ${boxShadowlayerColor1}`
          : "none",
        borderRadius: "8px",
        border: isDarkTheme ? "" : "1px solid #D7E5F8",
        position: "relative",
      }}
      data-testid="card-component"
    >
      <Box
        sx={{
          ...cardContainer,
          background: insideCardBackgroundColorLayer1,
          paddingBottom: "calc(48px + var(--Spacing-spacing-12, 0px))", // ✅ key line
          gap: "var(--Spacing-spacing-12, 12px)",
        }}
        // className={isMobile ? "!p-[8px]" : ""}
      >
        <Box
          sx={{
            position: "relative", // IMPORTANT: allows absolute children
            width: "100%",
            display: "flex",
            padding: "0",
            flexDirection: "column",
            alignItems: "flex-start",
            alignSelf: "stretch",
          }}
        >
          {/* Lazy Image */}
          <LazyImage id={card?.thumbnailFileId} url={card?.cdnUrl} />

          {/* Overlay content */}
          {chapter && <RelevanceScore card={card} />}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--Spacing-spacing-8, 8px)",
            alignSelf: "stretch",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: textColorLayer2,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "var(--Typography-Font-Family-Heading, Inter)",
              fontSize: isMobile ? "18px" : "1.2rem",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "var(--Line-Height-H5, 32px)", // 133.333%
              alignSelf: "stretch",
              minHeight: "64px", // 2 lines * lineHeight (32px)
              maxHeight: "64px",
              overflow: "hidden", // Hides overflowing content
              textOverflow: "ellipsis", // Adds ellipsis
              display: "-webkit-box", // Required for multiline ellipsis
              WebkitBoxOrient: "vertical", // Required for multiline ellipsis
              WebkitLineClamp: 2, // Limit to 2 lines
            }}
          >
            {card?.title}
          </Typography>
          <Typography
            sx={{
              color: textColorLayer2,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "var(--Typography-Font-Family-Body, Inter)",
              fontSize: "1.1rem",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "var(--Line-Height-Body, 22px)", // 137.5%
              minHeight: "44px", // 2 lines * lineHeight (32px)
              maxHeight: "44px",
              overflow: "hidden", // Hides overflowing content
              textOverflow: "ellipsis", // Adds ellipsis
              display: "-webkit-box", // Required for multiline ellipsis
              WebkitBoxOrient: "vertical", // Required for multiline ellipsis
              WebkitLineClamp: 2, // Limit to 2 lines
              alignSelf: "stretch",
            }}
            dangerouslySetInnerHTML={{
              __html: card?.highlights?.summary
                ? card?.highlights?.summary[0]
                : card?.description || "",
            }}
          />

          <Box
            sx={
              isMobile
                ? {
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }
                : {
                    display: "flex",
                    width: "100%",
                    alignItems: "flex-start",
                    gap: "var(--Spacing-spacing-12, 12px)",
                    flexDirection: flexDirection,
                  }
            }
          >
            <Box
              sx={{
                width: isMobile ? "100%" : "auto",
                flexShrink: 0,
                display: "flex",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  marginRight: "2px",
                  lineHeight: "var(--Line-Height-Body, 18px)", // 128.571%
                }}
              >
                {"Publication Date"} :
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: "var(--Font-Size-Body, 14px)",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "var(--Line-Height-Body, 18px)",
                }}
              >
                {getFormattedDate(heading, card, formatDate)}
              </Typography>
            </Box>
            {chapter && <Chapter highlights={card?.highlights} />}
            <Box
              sx={{
                width: isMobile ? "100%" : "40%",
                flexShrink: 0,
                display: "flex",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  marginRight: "2px",
                  lineHeight: "var(--Line-Height-Body, 18px)", // 128.571%
                }}
              >
                View Count:
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: "var(--Font-Size-Body, 14px)",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "var(--Line-Height-Body, 18px)",
                }}
              >
                {displayCount}
              </Typography>
            </Box>
            <Box
              sx={{
                width: isMobile ? "100%" : "20%",
                flexShrink: 0,
                display: "flex",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  marginRight: "2px",
                  lineHeight: "var(--Line-Height-Body, 18px)", // 128.571%
                }}
              >
                Likes:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: textColorLayer2,
                  fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                  fontSize: "var(--Font-Size-Body, 14px)",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "var(--Line-Height-Body, 18px)",
                }}
              >
                {card?.likes !== null ? card?.likes : 0}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            alignSelf: "stretch",
            position: "absolute",
            bottom: "0px",
            left: "0",
            right: "0",
            padding: "8px 12px ", // keeps spacing nice
          }}
        >
          <div className="flex flex-row gap-3 h-full">
            <Box
              sx={linkContainer}
              onClick={
                !cardButtonDisable
                  ? () => handleAddFavourite(card, content)
                  : undefined
              }
              data-testid="favourite"
            >
              {isFavourite ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 18 17"
                  fill="none"
                >
                  <path
                    d="M11.3417 6.18366L17.3334 6.70033L12.7917 10.642L14.15 16.5003L9.00002 13.392L3.85002 16.5003L5.21669 10.642L0.666687 6.70033L6.65835 6.19199L9.00002 0.666992L11.3417 6.18366Z"
                    fill="#005CBC"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M18.3334 7.70033L12.3417 7.18366L10 1.66699L7.65835 7.19199L1.66669 7.70033L6.21669 11.642L4.85002 17.5003L10 14.392L15.15 17.5003L13.7917 11.642L18.3334 7.70033ZM10 12.8337L6.86669 14.7253L7.70002 11.1587L4.93335 8.75866L8.58335 8.44199L10 5.08366L11.425 8.45033L15.075 8.76699L12.3084 11.167L13.1417 14.7337L10 12.8337Z"
                    fill="#005CBC"
                  />
                </svg>
              )}
              <Typography
                sx={
                  isMobile || isZoomed || isMedium
                    ? { display: "none" }
                    : linkTypography
                }
              >
                Favorite
              </Typography>
            </Box>
            <Box
              sx={linkContainer}
              onClick={
                !cardButtonDisable ? () => handleOpenSummary(card) : undefined
              }
              data-testid="open-summary-button"
            >
              <RemoveRedEyeOutlinedIcon
                sx={{
                  fill: "#005CBC",
                  width: "20px",
                  height: "20px",
                }}
              />
              <Typography
                sx={
                  isMobile || isZoomed || isMedium
                    ? { display: "none" }
                    : linkTypography
                }
              >
                View Summary
              </Typography>
            </Box>
          </div>
          <Button
            sx={buttonStyle}
            onClick={
              !cardButtonDisable
                ? () => handleNavigate(card, singularType)
                : undefined
            }
            data-testid="navigate"
            disabled={cardButtonDisable}
          >
            <Typography
              sx={{
                color: "var(--Tokens-Button-Borderless, #005CBC)",
                fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                fontSize: ".75rem",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "var(--Line-Height-Small, 20px)",
                textTransform: "none",
              }}
            >
              View {singularType}
            </Typography>

            {isLink ? (
              <LaunchOutlinedIcon
                sx={{
                  fill: "#005CBC",
                  width: "20px",
                  height: "20px",
                }}
              />
            ) : (
              <ArrowForwardIcon
                sx={{
                  fill: "#005CBC",
                  width: "20px",
                  height: "20px",
                }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
CardComponent.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    favorite: PropTypes.bool,
    contentId: PropTypes.number,
    likes: PropTypes.number,
    counts: PropTypes.string,
    views: PropTypes.string,
    uploadedDate: PropTypes.string,
    viewCount: PropTypes.string,
    uploadedOn: PropTypes.string,
    date: PropTypes.string,
  }).isRequired,
  content: PropTypes.node.isRequired,
  flexDirection: PropTypes.oneOf(["row", "column"]),
  heading: PropTypes.string.isRequired,
  cardButtonDisable: PropTypes.bool, // Boolean flag for disbale click event for view video and view summary
};
